<template>
    <div
        v-if="isValid"
        class="moreMarketDisplay moreMarketBetBuilderCombination notranslate"
        v-bind:class="{ __live: isLive }"
    >
        <button
            type="button"
            class="moreMarketDisplay_type"
            v-bind:class="{ _isMarketShow: isSectionExpanded }"
            v-on:click="toggleSectionExpand"
        >
            <span class="moreMarketBetBuilderCombination_header">
                <SboIcon
                    name="system:fire"
                    class="hotIcon"
                    size="16"
                />
                <span>{{ sectionTitle }}</span>
            </span>
            <SvgIcon iconName="systemIcon_arrowBottom" />
        </button>
        <TransitionCollapse>
            <div
                v-if="isSectionExpanded"
                class="moreMarketDisplay_columnWrapper"
            >
                <div class="moreMarketBetBuilderCombination_body">
                    <div class="moreMarketBetBuilderCombination_content">
                        <ul>
                            <BetBuilderCombinationTicket
                                v-for="item in selections"
                                v-bind:key="item.selection.id"
                                v-bind:selection="item.selection"
                                v-bind:market="item.market"
                                v-bind:event="event"
                            />
                        </ul>
                    </div>
                    <div class="moreMarketBetBuilderCombination_actions">
                        <span
                            class="moreMarketBetBuilderCombination_odds"
                            v-bind:class="{ 'odds-change': oddsChanging }"
                        >
                            <SboIcon
                                v-if="isSuspended"
                                name="system:lock"
                                class="locked"
                                size="16"
                            />
                            <template v-else>
                                @{{ maxOdds }}
                            </template>
                        </span>
                        <Cta
                            v-bind:customClass="btnClass"
                            v-bind:ga="addToSlipGa"
                            v-bind:disabled="isSuspended"
                            v-bind:onClick="addToBetSlip"
                        >
                            <strong>{{ addToSlipText }}</strong>
                        </Cta>
                    </div>
                </div>
            </div>
        </TransitionCollapse>
    </div>
</template>

<script lang="ts" setup>
    import { refAutoReset } from '@vueuse/core';
    import { storeToRefs } from 'pinia';
    import { computed, onUnmounted, ref, toRefs, watch } from 'vue';
    import Cta from '@/components/common/Cta.vue';
    import SboIcon from '@/components/common/SboIcon.vue';
    import TransitionCollapse from '@/components/common/TransitionCollapse.vue';
    import BetBuilderCombinationTicket from '@/components/recommendations/BetBuilderCombinationTicket.vue';
    import type { IBetBuilderRecommendation, IRecommendationEvent } from '@/interface/IBetBuilderRecommendation';
    import { BetBuilderSelectionStatus, BetPageType, MarketTab } from '@/interface/enum';
    import { platform } from '@/libraries/helpers/platformHelper';
    import { i18n } from '@/libraries/i18n';
    import { useBetBuilderHub } from '@/libraries/recommendation/useBetBuilderHub';
    import type { IPreBuiltSelectionWithMarket } from '@/libraries/recommendation/useMatchPreBuiltRecommendation';
    import { formatMinDecimalPlace } from '@/libraries/utils/text';
    import { useBetBuilderBetSlipStore } from '@/stores/betSlip/betBuilderBetSlipStore';
    import { useBetSlipStore } from '@/stores/betSlip/betSlipStore';
    import { useSingleModeBetSlipStore } from '@/stores/betSlip/singleModeBetSlipStore';
    import { useRootStore } from '@/stores/rootStore';
    import SvgIcon from '@/svg/SvgIcon.vue';

    interface Props {
        matchId: number;
        isLive?: boolean;
        isCollapse?: boolean;
        recommendation?: IBetBuilderRecommendation | null;
        event?: IRecommendationEvent | null;
        selections?: IPreBuiltSelectionWithMarket[];
    }

    const props = withDefaults(defineProps<Props>(), {
        isLive: false,
        isCollapse: false,
        recommendation: null,
        event: null,
        selections: () => [],
    });

    const { event, recommendation, selections } = toRefs(props);

    const isSectionExpanded = ref(true);

    function toggleSectionExpand() {
        isSectionExpanded.value = !isSectionExpanded.value;
    }

    watch(
        () => props.isCollapse,
        (newValue) => {
            isSectionExpanded.value = newValue;
        },
    );

    const { isAsi } = storeToRefs(useRootStore());
    const isEnabledWs = computed(() => isSectionExpanded.value && isAsi.value);

    const isSuspended = computed(() => selections.value.some(s => s.selection.status === BetBuilderSelectionStatus.Suspended));

    const isDeleted = ref(false);

    watch(
        recommendation,
        () => {
            isDeleted.value = false;
        },
    );

    const isValid = computed(() => {
        const rec = recommendation.value;
        if (!rec || isDeleted.value) return false;
        return event.value !== null
            && rec.selectionIds !== undefined
            && selections.value.length === rec.selectionIds.length
            && selections.value.every(s => s.selection.status !== BetBuilderSelectionStatus.Closed);
    });

    const realtimeMaxOdds = ref(0);
    watch(
        () => recommendation.value?.maxOdds,
        (odds) => {
            realtimeMaxOdds.value = odds ?? 0;
        },
        { immediate: true },
    );

    const { subscribe: subscribeBetBuilder, unsubscribe: unsubscribeBetBuilder } = useBetBuilderHub();
    const hubCallbacks = {
        onUpdate: (payload: IBetBuilderRecommendation) => {
            realtimeMaxOdds.value = payload.maxOdds;
        },
        onDelete: () => {
            isDeleted.value = true;
        },
    };

    let pendingSubscribe = Promise.resolve();

    watch(
        [isEnabledWs, () => recommendation.value?.combinationKey],
        async ([enabled, key], [oldEnabled, oldKey]) => {
            const prevEnabled = oldEnabled ?? false;
            const prevKey = oldKey ?? '';
            if (prevEnabled && prevKey) {
                await pendingSubscribe;
                await unsubscribeBetBuilder(prevKey, hubCallbacks);
            }
            if (enabled && key) {
                pendingSubscribe = subscribeBetBuilder(key, hubCallbacks);
                await pendingSubscribe;
            }
        },
        { immediate: true },
    );

    const sectionTitle = computed(() => i18n.t('popular_bet_builder_card_title'));
    const addToSlipText = computed(() => i18n.t('popular_combination_button_add_to_slip'));
    const maxOdds = computed(() => formatMinDecimalPlace(realtimeMaxOdds.value, 2));
    const addToSlipGa = computed(() => 'MoreMarket_Btn_PreBuilt_AddToSlip');
    const btnClass = computed(() => ['btn', 'btn-accent', isSuspended.value ? '_disabled' : '']);

    const { addTicket, removeBetSlip, setOpenFrom } = useBetBuilderBetSlipStore();
    const { showMediumBetSlip } = useBetSlipStore();
    const { resetSingleModeForBetBuilder } = useSingleModeBetSlipStore();

    const oddsChanging = refAutoReset(false, 3000);

    watch(maxOdds, (newVal, oldVal) => {
        if (newVal !== oldVal) {
            oddsChanging.value = true;
        }
    });

    onUnmounted(() => {
        const key = recommendation.value?.combinationKey;
        if (key && isEnabledWs.value) {
            unsubscribeBetBuilder(key, hubCallbacks);
        }
    });

    async function addToBetSlip() {
        if (!isAsi.value) {
            platform.showLogin('Sports');
            return;
        }

        const currentEvent = event.value;
        if (isSuspended.value) return;
        const rec = recommendation.value;
        if (!currentEvent || !rec || selections.value.length !== (rec.selectionIds?.length ?? 0)) return;

        await removeBetSlip();
        resetSingleModeForBetBuilder();

        selections.value.forEach(({ selection, market }) => {
            addTicket(selection, currentEvent, market);
        });

        setOpenFrom(BetPageType.BetBuilderRecommendation_MoreMarket);
        window.eventEmitter.emit('changeMarketTab', props.matchId, MarketTab.BetBuilder);
        showMediumBetSlip();
    }
</script>

<style lang="scss" scoped>
@import "@/scss/components/moreMarket";
@import "@/scss/components/moreMarketBetBuilderCombination";
@import "@/scss/components/quickCombination";
@import "@/scss/components/quickCombinationTicket";
</style>
